/**
 * Staff manual order entry (SA-49) — single modal.
 * Catalog picker + contact linking are implemented here (SA-53).
 * Deposits / payment records: SA-185. Edit: SA-187. Payment section: SA-291.
 * SA-292: rebuilt on the design system — ModalBody/ModalFooter shell,
 * kit controls, sectioned layout, inline field validation, success toasts.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription,
  ModalBody, ModalFooter, Button, Input, NativeSelect, Textarea, Badge,
  useToast,
} from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import { formatCurrency } from '@/utils/currency'
import { useFeature } from '@/hooks/useFeature'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { CatalogPicker, type CatalogProduct } from './CatalogPicker'
import { ContactSearch, type ContactHit } from './ContactSearch'
import type { Order, OrderCreatePayload, OrderItem, OrderUpdatePayload } from '@/types/commerce'

interface PersonaOption { id: string; name: string }

interface LineItem { title: string; quantity: string; unitPrice: string; productId?: string }

interface NewOrderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate?: (payload: OrderCreatePayload) => Promise<void>
  /** SA-187 edit mode: present = editing this order. */
  order?: Order | null
  onSave?: (orderId: string, payload: OrderUpdatePayload) => Promise<void>
  /** SA-291 edit mode: record a completed payment against the SAVED order
   *  (rides POST /payments — payment as evidence, the primary path). */
  onRecordPayment?: (orderId: string, p: { amountCents: number; method: string }) => Promise<void>
  /** E4 (SA-389, spec D26): catalog items and every payment field are
   *  commerce. Off = the edit form of a record deployment (customer,
   *  fulfillment, quoted amount, notes). Create is commerce-only end to end
   *  (POST /orders takes at least one item), so the Orders page never opens
   *  create mode with this off. Defaults on. */
  commerceEnabled?: boolean
}

const EMPTY_ITEM: LineItem = { title: '', quantity: '1', unitPrice: '' }

function toCents(pesos: string): number {
  const s = pesos.trim()
  if (!/^\d+(\.\d+)?$/.test(s)) return 0
  const [whole, frac = ''] = s.split('.')
  const fracCents = frac.padEnd(2, '0')
  const base = parseInt(whole, 10) * 100 + parseInt(fracCents.slice(0, 2) || '0', 10)
  return fracCents.length > 2 && Number(fracCents[2]) >= 5 ? base + 1 : base
}

/** SA-187: format a UTC ISO string as the LOCAL wall-clock value a
 *  `datetime-local` input expects ("YYYY-MM-DDTHH:mm"). The create path
 *  does the opposite conversion (`new Date(deliveryDate).toISOString()`
 *  interprets the input's digits as local time and stores UTC) — a raw
 *  `.slice(0, 16)` of the UTC string would instead render the UTC digits
 *  as if they were local, shifting the displayed time in any non-UTC
 *  deployment. Both hydration and the payload builder's diff baseline
 *  must use this same helper so an untouched field never spuriously diffs. */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** SA-187: shared per-item normalization, reused by both
 *  buildUpdatePayload's new-vs-old items diff (decides what rides in the
 *  PATCH body) and the itemsChanged gate below (decides whether edit mode
 *  enforces item-row validation at all) — one mapping pair, two call
 *  sites, so the two never drift apart. */
function normalizeFormItem(it: LineItem) {
  return {
    title: it.title.trim(),
    quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
    unit_price_cents: toCents(it.unitPrice),
    ...(it.productId ? { product_id: it.productId } : {}),
  }
}

function normalizeOrderItem(it: OrderItem) {
  return {
    title: String(it.title ?? it.name ?? ''),
    quantity: Number(it.quantity ?? 1),
    unit_price_cents: it.unit_price != null ? Number(it.unit_price)
      : it.price != null ? Math.round(Number(it.price) * 100) : 0,
    ...(it.product_id ? { product_id: String(it.product_id) } : {}),
  }
}

/** A row that still matches the untouched placeholder shape (EMPTY_ITEM,
 *  or "Add item"'s freshly appended row) — i.e. the user hasn't put
 *  anything into it yet. Item-less orders hydrate one of these so the
 *  form has a row to show; it must NOT count toward "did items change"
 *  (SA-187 review: it was wrongly gating a bare customer-name edit on an
 *  item-less order behind "Every item needs a name"). Any deviation from
 *  this shape — including a price typed into an otherwise-blank row —
 *  DOES count, so a touched-but-title-less row still gets caught by
 *  validate's title check rather than silently vanishing from the diff. */
function isPristineItemRow(it: LineItem): boolean {
  return !it.title.trim() && !it.unitPrice.trim() &&
    it.quantity === EMPTY_ITEM.quantity && !it.productId
}

/** The editable-field slice of form state, as one value — what hydration
 *  writes and what the payload builder reads. Extracted (SA-292) so the
 *  dirty guard can evaluate the payload builder against the hydrated
 *  baseline as well as the live form. */
interface FormSnapshot {
  customerName: string
  customerPhone: string
  items: LineItem[]
  method: 'pickup' | 'delivery'
  address: string
  pickupLocation: string
  deliveryDate: string
  deliveryFee: string
  deposit: string
  instructions: string
  quotedAmount: string
}

/** SA-187: the edit-mode hydration mapping, order → form fields. The
 *  single source for both the hydration effect and the dirty-guard
 *  baseline, so the two can never disagree. */
function hydrateFromOrder(order: Order): FormSnapshot {
  const srcItems = order.items && order.items.length > 0 ? order.items : null
  return {
    customerName: order.customerName ?? '',
    customerPhone: order.customerPhone ?? '',
    items: srcItems
      ? srcItems.map(it => ({
          title: String(it.title ?? it.name ?? ''),
          quantity: String(it.quantity ?? 1),
          unitPrice: it.unit_price != null ? String(Number(it.unit_price) / 100)
            : it.price != null ? String(Number(it.price)) : '',
          ...(it.product_id ? { productId: String(it.product_id) } : {}),
        }))
      : [{ ...EMPTY_ITEM }],
    method: (order.deliveryMethod as 'pickup' | 'delivery') ?? 'pickup',
    address: order.deliveryAddress ?? '',
    pickupLocation: order.pickupLocation ?? '',
    // deliveryDate binds a datetime-local input, which browsers treat as
    // LOCAL wall-clock — order.deliveryDate is a UTC ISO string, so it
    // must be converted to local components (toLocalInputValue), not
    // sliced raw.
    deliveryDate: order.deliveryDate ? toLocalInputValue(order.deliveryDate) : '',
    deliveryFee: order.deliveryFeeCents ? String(order.deliveryFeeCents / 100) : '',
    deposit: order.depositCents ? String(order.depositCents / 100) : '',
    instructions: order.specialInstructions ?? '',
    quotedAmount: order.quotedAmountCents != null
      ? String(order.quotedAmountCents / 100) : '',
  }
}

/** SA-187: only changed fields ride, plus the always-present echo key
 *  (expected_total_cents) the backend uses to detect a stale edit.
 *  Cleared optional fields go through as explicit null. */
function buildUpdatePayloadFrom(o: Order, s: FormSnapshot): OrderUpdatePayload {
  const p: OrderUpdatePayload = { expected_total_cents: o.totalCents }

  const name = s.customerName.trim()
  if (name && name !== (o.customerName ?? '')) p.customer_name = name

  const phone = s.customerPhone.trim()
  if (phone !== (o.customerPhone ?? '')) p.customer_phone = phone || null

  const newItems = s.items.filter(it => it.title.trim()).map(normalizeFormItem)
  const oldItems = (o.items ?? []).map(normalizeOrderItem)
  if (JSON.stringify(newItems) !== JSON.stringify(oldItems)) p.items = newItems

  const oldMethod = (o.deliveryMethod as 'pickup' | 'delivery') ?? 'pickup'
  if (s.method !== oldMethod) p.delivery_method = s.method
  if (s.method === 'pickup') {
    if (o.deliveryAddress) p.delivery_address = null
    if (o.deliveryFeeCents) p.delivery_fee_cents = 0
    const loc = s.pickupLocation.trim()
    if (loc !== (o.pickupLocation ?? '')) p.pickup_location = loc || null
  } else {
    const addr = s.address.trim()
    if (addr !== (o.deliveryAddress ?? '')) p.delivery_address = addr
    const fee = toCents(s.deliveryFee)
    if (fee !== o.deliveryFeeCents) p.delivery_fee_cents = fee
  }

  // Baseline must use the SAME conversion as hydration (toLocalInputValue)
  // so an untouched field never spuriously diffs; the outgoing conversion
  // matches the create path's own expression exactly
  // (`new Date(deliveryDate).toISOString()` — the datetime-local input's
  // digits are interpreted as local time and stored as UTC).
  const oldDeliveryDate = o.deliveryDate ? toLocalInputValue(o.deliveryDate) : ''
  if (s.deliveryDate !== oldDeliveryDate)
    p.delivery_date = s.deliveryDate ? new Date(s.deliveryDate).toISOString() : null

  const dep = toCents(s.deposit)
  if (dep !== o.depositCents) p.deposit_cents = dep

  const si = s.instructions.trim()
  if (si !== (o.specialInstructions ?? '')) p.special_instructions = si || null

  if (o.quotedAmountCents != null) {
    const q = toCents(s.quotedAmount)
    if (q !== o.quotedAmountCents) p.quoted_amount_cents = q
  }

  return p
}

/** Submit-time validation results, keyed by the field (or section) that
 *  shows them inline. `itemsKind` records WHICH item rule tripped so the
 *  offending row inputs can be marked aria-invalid. */
interface FieldErrors {
  customerName?: string
  items?: string
  itemsKind?: 'title' | 'quantity' | 'price'
  address?: string
  deposit?: string
  payMethod?: string
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h3 className="text-sm font-semibold text-foreground">{children}</h3>
}

export function NewOrderModal({
  open, onOpenChange, onCreate, order, onSave, onRecordPayment, commerceEnabled = true,
}: NewOrderModalProps) {
  const isEdit = order != null
  // E4 (SA-397): the modal's title and toasts wear the deployment's noun.
  const noun = useRecordNoun()
  const crmEnabled = useFeature('crm')
  const { addToast } = useToast()
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [linkedContact, setLinkedContact] = useState<ContactHit | null>(null)
  const [items, setItems] = useState<LineItem[]>([{ ...EMPTY_ITEM }])
  const [method, setMethod] = useState<'pickup' | 'delivery'>('pickup')
  const [address, setAddress] = useState('')
  const [pickupLocation, setPickupLocation] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [deliveryFee, setDeliveryFee] = useState('')
  const [deposit, setDeposit] = useState('')
  const [payMethod, setPayMethod] = useState('')
  const [status, setStatus] = useState<'pending_payment' | 'deposit_paid' | 'fully_paid'>('pending_payment')
  const [instructions, setInstructions] = useState('')
  const [notes, setNotes] = useState('')
  /** SA-187: hydrated only for quote-driven orders; sent as quoted_amount_cents. */
  const [quotedAmount, setQuotedAmount] = useState('')
  const [personas, setPersonas] = useState<PersonaOption[]>([])
  const [personaId, setPersonaId] = useState('')
  const [products, setProducts] = useState<CatalogProduct[] | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [requestError, setRequestError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer')
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [recordingPayment, setRecordingPayment] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiClient.fetch('/api/personas')
        if (!res.ok || cancelled) return
        const list = (await res.json()) as { id: string; name: string }[]
        if (cancelled) return
        setPersonas(list.map(p => ({ id: p.id, name: p.name })))
        if (list.length >= 1) setPersonaId(prev => prev || list[0].id)
      } catch { /* persona attach is optional */ }
    })()
    // The catalog is commerce (the products router is unmounted without it).
    if (commerceEnabled) (async () => {
      try {
        const res = await apiClient.fetch('/api/commerce/products')
        if (!res.ok || cancelled) { setProducts(null); return }
        // GET /api/commerce/products returns ProductListResponse
        // { items, total } — never a bare array.
        const data = await res.json() as { items?: CatalogProduct[] }
        if (cancelled) return
        setProducts(Array.isArray(data.items) ? data.items : null)
      } catch { if (!cancelled) setProducts(null) }
    })()
    if (order) {
      // SA-187: re-hydrate every form field from the order being edited.
      // resetForm() stays create-only — this is the edit-mode equivalent,
      // run on open (and if the order reference itself changes).
      const h = hydrateFromOrder(order)
      setCustomerName(h.customerName)
      setCustomerPhone(h.customerPhone)
      setItems(h.items)
      setMethod(h.method)
      setAddress(h.address)
      setPickupLocation(h.pickupLocation)
      setDeliveryDate(h.deliveryDate)
      setDeliveryFee(h.deliveryFee)
      setDeposit(h.deposit)
      setInstructions(h.instructions)
      setQuotedAmount(h.quotedAmount)
      // SA-291: prefill the record-payment form to the outstanding balance;
      // this re-runs after a successful payment (fresh `order` prop).
      const completed = (order.payments ?? [])
        .filter(p => p.status === 'completed')
        .reduce((sum, p) => sum + p.amountCents, 0)
      setPaymentAmount((Math.max(order.totalCents - completed, 0) / 100).toFixed(2))
      setPaymentMethod('bank_transfer')
      setPaymentError(null)
    }
    return () => { cancelled = true }
  }, [open, order, commerceEnabled])

  const subtotalCents = useMemo(
    () => items.reduce((sum, it) =>
      sum + (parseInt(it.quantity, 10) || 0) * toCents(it.unitPrice), 0),
    [items],
  )
  const feeCents = method === 'delivery' ? toCents(deliveryFee) : 0
  // SA-187: deposit presets need a base total; for a quote-driven order
  // (edit mode with a quoted amount) that's the quoted amount, not the
  // line-item sum, since items are informational once a quote is set.
  const totalCents = isEdit && order?.quotedAmountCents != null
    ? toCents(quotedAmount)
    : subtotalCents + feeCents
  const depositCents = toCents(deposit)

  // SA-291: balance summary is over the SAVED order — completed payments only.
  const completedPaymentsCents = (order?.payments ?? [])
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amountCents, 0)
  const balanceDueCents = Math.max((order?.totalCents ?? 0) - completedPaymentsCents, 0)

  /** SA-187 review fix: whether the items section actually differs from
   *  the order baseline — untouched placeholder rows (isPristineItemRow)
   *  don't count, so an item-less order's single synthetic empty row
   *  doesn't wrongly gate an unrelated edit (e.g. customer name) behind
   *  item-row validation. Any real deviation counts, including a row
   *  whose title is still blank, so validate's title check still catches
   *  it instead of it silently disappearing from buildUpdatePayload's
   *  title-filtered diff. */
  const itemsChanged = useMemo(() => {
    if (!isEdit || !order) return false
    const newItems = items.filter(it => !isPristineItemRow(it)).map(normalizeFormItem)
    const oldItems = (order.items ?? []).map(normalizeOrderItem)
    return JSON.stringify(newItems) !== JSON.stringify(oldItems)
  }, [isEdit, order, items])

  useEffect(() => {
    if (status === 'deposit_paid' && depositCents <= 0) setStatus('pending_payment')
  }, [status, depositCents])

  const setItem = (i: number, patch: Partial<LineItem>) =>
    setItems(prev => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))

  const handlePick = (p: CatalogProduct) =>
    setItems(prev => [...prev,
      { title: p.name, quantity: '1',
        unitPrice: p.price_cents != null ? String(p.price_cents / 100) : '',
        productId: p.id }])

  /** SA-187: `editMode` skips checks that don't apply to edits — the
   *  quantity regex (edit mode's buildUpdatePayload already coerces bad
   *  quantities via Math.max(1, parseInt(...) || 1), so a strict block
   *  here would newly refuse input the payload builder already tolerates)
   *  and the payment-method check (that field isn't rendered in edit mode,
   *  and `status` never leaves its initial 'pending_payment' there).
   *  Review fix: the item-row checks (title, price) only apply in edit
   *  mode when `itemsChanged` — otherwise an item-less order's untouched
   *  placeholder row (hydration seeds one so the form has something to
   *  show) would block an unrelated edit, like a bare customer-name fix,
   *  behind "Every item needs a name" for a row the staff user never
   *  touched. All other checks — delivery-without-address, deposit >=
   *  total, empty customer name — are shared verbatim with create mode
   *  and stay unconditional. SA-292: results render inline at each field
   *  (one message per section), no longer as a single bottom line. */
  const validate = (opts?: { editMode?: boolean; itemsChanged?: boolean }): FieldErrors => {
    const errs: FieldErrors = {}
    const checkItems = !opts?.editMode || opts.itemsChanged
    if (!customerName.trim()) errs.customerName = 'Customer name is required'
    if (checkItems && items.some(it => !it.title.trim())) {
      errs.items = 'Every item needs a name'; errs.itemsKind = 'title'
    } else if (!opts?.editMode && items.some(it => !/^[1-9]\d*$/.test(it.quantity.trim()))) {
      errs.items = 'Quantities must be whole numbers of 1 or more'; errs.itemsKind = 'quantity'
    } else if (checkItems && items.some(it => toCents(it.unitPrice) <= 0)) {
      errs.items = 'Every item needs a unit price'; errs.itemsKind = 'price'
    }
    if (method === 'delivery' && !address.trim()) errs.address = 'Delivery address is required'
    if (depositCents > 0 && depositCents >= totalCents)
      errs.deposit = 'Deposit must be less than the total'
    if (!opts?.editMode && status !== 'pending_payment' && !payMethod)
      errs.payMethod = 'Payment method is required for paid orders'
    return errs
  }

  /** Whether an item row's input of the tripped kind should be flagged. */
  const itemRowInvalid = (it: LineItem, kind: FieldErrors['itemsKind']): boolean => {
    if (!fieldErrors.items || fieldErrors.itemsKind !== kind) return false
    if (kind === 'title') return !it.title.trim()
    if (kind === 'quantity') return !/^[1-9]\d*$/.test(it.quantity.trim())
    return toCents(it.unitPrice) <= 0
  }

  const resetForm = () => {
    setCustomerName(''); setCustomerPhone(''); setLinkedContact(null)
    setItems([{ ...EMPTY_ITEM }])
    setMethod('pickup'); setAddress(''); setPickupLocation('')
    setDeliveryDate(''); setDeliveryFee(''); setStatus('pending_payment')
    setDeposit(''); setPayMethod('')
    setInstructions(''); setNotes(''); setPersonaId('')
    setFieldErrors({}); setRequestError(null)
  }

  const snapshot: FormSnapshot = {
    customerName, customerPhone, items, method, address, pickupLocation,
    deliveryDate, deliveryFee, deposit, instructions, quotedAmount,
  }

  const buildUpdatePayload = (): OrderUpdatePayload =>
    buildUpdatePayloadFrom(order!, snapshot)

  /** SA-291/SA-292: record-payment is an immediate action against the SAVED
   *  order — any unsaved edit both races the PATCH's expected_total_cents
   *  stale-echo guard (money fields) and would be silently discarded when
   *  the payment response re-hydrates the form (every field). Dirty means
   *  the payload builder's output DIVERGES FROM THE HYDRATED BASELINE, not
   *  merely that it holds keys: a just-hydrated form can already self-diff
   *  (e.g. pickup-residue orders, where the builder emits residue clears
   *  for a stale delivery address/fee with zero user edits) and must not
   *  lock payments behind a save the staff user never made. itemsChanged
   *  additionally catches title-less item-row deviations, which the
   *  payload's title-filtered items diff deliberately drops. */
  const hydratedBaseline = useMemo(
    () => order ? JSON.stringify(buildUpdatePayloadFrom(order, hydrateFromOrder(order))) : '',
    [order],
  )
  const formDirty = isEdit &&
    (itemsChanged || JSON.stringify(buildUpdatePayloadFrom(order!, snapshot)) !== hydratedBaseline)

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void handleSubmit()
  }

  const handleSubmit = async () => {
    if (saving) return
    if (isEdit) {
      if (!onSave) return
      const problems = validate({ editMode: true, itemsChanged })
      if (Object.keys(problems).length > 0) { setFieldErrors(problems); return }
      setFieldErrors({}); setRequestError(null)
      setSaving(true)
      try {
        await onSave(order!.id, buildUpdatePayload())
        addToast({ type: 'success', title: `${noun.singular} saved` })
        onOpenChange(false)
      } catch (e) { setRequestError((e as Error).message) } finally { setSaving(false) }
      return
    }
    if (!onCreate) return
    const problems = validate()
    if (Object.keys(problems).length > 0) { setFieldErrors(problems); return }
    setFieldErrors({}); setRequestError(null)
    setSaving(true)
    try {
      const payload: OrderCreatePayload = {
        customer_name: customerName.trim(),
        ...(customerPhone.trim() ? { customer_phone: customerPhone.trim() } : {}),
        ...(linkedContact ? { contact_id: linkedContact.id } : {}),
        ...(personaId ? { persona_id: personaId } : {}),
        items: items.map(it => ({
          title: it.title.trim(),
          quantity: parseInt(it.quantity.trim(), 10),
          unit_price_cents: toCents(it.unitPrice),
          ...(it.productId ? { product_id: it.productId } : {}),
        })),
        delivery_method: method,
        ...(method === 'delivery'
          ? { delivery_address: address.trim(),
              ...(feeCents ? { delivery_fee_cents: feeCents } : {}) }
          : pickupLocation.trim() ? { pickup_location: pickupLocation.trim() } : {}),
        ...(deliveryDate ? { delivery_date: new Date(deliveryDate).toISOString() } : {}),
        ...(depositCents > 0 ? { deposit_cents: depositCents } : {}),
        status,
        ...(status !== 'pending_payment'
          ? { payment_method: payMethod as 'cash' | 'card' | 'bank_transfer' | 'other' }
          : {}),
        ...(instructions.trim() ? { special_instructions: instructions.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }
      await onCreate(payload)
      addToast({ type: 'success', title: `${noun.singular} created` })
      onOpenChange(false)
      resetForm()
    } catch (e) {
      setRequestError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const canRecordPayment = !formDirty && !recordingPayment && toCents(paymentAmount) > 0

  const handleRecordPayment = async () => {
    if (!order || !onRecordPayment || recordingPayment) return
    setPaymentError(null)
    setRecordingPayment(true)
    try {
      await onRecordPayment(order.id, {
        amountCents: toCents(paymentAmount), method: paymentMethod,
      })
      addToast({ type: 'success', title: 'Payment recorded' })
    } catch (e) {
      setPaymentError((e as Error).message)
    } finally {
      setRecordingPayment(false)
    }
  }

  /** SA-292 (SA-291 deferred): Enter in the payment mini-form must trigger
   *  record-payment, never the surrounding form's implicit submission —
   *  the payment action targets the SAVED order, the form submit would
   *  save-and-close. */
  const handlePaymentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (canRecordPayment) void handleRecordPayment()
  }

  const quotedAmountField = isEdit && order?.quotedAmountCents != null ? (
    <Input label="Quoted amount" type="number" min={0} step="0.01"
      value={quotedAmount} onChange={e => setQuotedAmount(e.target.value)} />
  ) : null

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>{isEdit ? `Edit ${noun.singular.toLowerCase()}` : `New ${noun.singular.toLowerCase()}`}</ModalTitle>
          {isEdit && order && (
            <ModalDescription>Order {order.orderNumber}</ModalDescription>
          )}
        </ModalHeader>
        {/* noValidate: validation is owned by validate() and rendered inline —
            native constraint validation must not intercept submission (it
            disagrees with our rules: `step="0.01"` flags prices like 0.575
            that toCents deliberately accepts and rounds, and happy-dom even
            flags integer values via a float-precision stepMismatch). */}
        <form noValidate onSubmit={handleFormSubmit}>
          <ModalBody className="space-y-6">
            {/* ---- Customer ---- */}
            <section className="space-y-3">
              <SectionHeading>Customer</SectionHeading>
              {!isEdit && crmEnabled && (linkedContact ? (
                <div className="flex items-center justify-between text-sm rounded-lg border border-border px-3 py-2">
                  <span>Linked: <span className="font-medium">
                    {linkedContact.display_name || linkedContact.channel_user_id}</span></span>
                  <Button type="button" variant="ghost" size="sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setLinkedContact(null)}>
                    Unlink
                  </Button>
                </div>
              ) : (
                <ContactSearch onSelect={c => {
                  setLinkedContact(c)
                  setCustomerName(prev => c.display_name || prev)
                  // whatsapp identity IS a phone; manual identity is one only when the
                  // SA-188 create path stored typed digits (uuid fallback otherwise).
                  if (c.channel === 'whatsapp' ||
                      (c.channel === 'manual' && /^\d+$/.test(c.channel_user_id)))
                    setCustomerPhone(c.channel_user_id)
                }} />
              ))}
              <div className="grid grid-cols-2 gap-3">
                <Input label="Customer name" required aria-required
                  error={fieldErrors.customerName}
                  value={customerName} onChange={e => setCustomerName(e.target.value)} />
                <Input label="Phone" aria-label="Customer phone"
                  value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
              </div>
              {!isEdit && personas.length >= 1 && (
                <NativeSelect label="Persona"
                  value={personaId} onChange={e => setPersonaId(e.target.value)}>
                  <option value="">No persona</option>
                  {personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </NativeSelect>
              )}
            </section>

            {/* ---- Items (commerce) ---- */}
            {commerceEnabled && (
            <section className="space-y-2">
              <SectionHeading>Items</SectionHeading>
              <div className="grid grid-cols-[minmax(0,1fr)_5rem_7rem_5.5rem_2rem] gap-2 text-xs text-muted-foreground">
                <span>Item *</span>
                <span>Qty</span>
                <span>Unit price *</span>
                <span className="text-right">Line total</span>
                <span />
              </div>
              {items.map((it, i) => {
                const lineCents = (parseInt(it.quantity, 10) || 0) * toCents(it.unitPrice)
                return (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_5rem_7rem_5.5rem_2rem] gap-2 items-center">
                    <Input aria-label={`Item ${i + 1} name`} aria-required placeholder="Item"
                      aria-invalid={itemRowInvalid(it, 'title') || undefined}
                      value={it.title}
                      onChange={e => setItem(i, { title: e.target.value })} />
                    <Input aria-label={`Item ${i + 1} quantity`} type="number" min={1}
                      aria-invalid={itemRowInvalid(it, 'quantity') || undefined}
                      value={it.quantity}
                      onChange={e => setItem(i, { quantity: e.target.value })} />
                    <Input aria-label={`Item ${i + 1} unit price`} aria-required type="number"
                      min={0} step="0.01" placeholder="$"
                      aria-invalid={itemRowInvalid(it, 'price') || undefined}
                      value={it.unitPrice}
                      onChange={e => setItem(i, { unitPrice: e.target.value })} />
                    <span className="text-sm text-right text-muted-foreground tabular-nums">
                      {lineCents > 0 ? formatCurrency(lineCents) : '—'}
                    </span>
                    {items.length > 1 ? (
                      <Button type="button" variant="ghost" size="sm"
                        aria-label={`Remove item ${i + 1}`}
                        className="text-muted-foreground hover:text-destructive px-2"
                        onClick={() => setItems(prev => prev.filter((_, idx) => idx !== i))}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : <span />}
                  </div>
                )
              })}
              {fieldErrors.items && (
                <p className="text-sm text-destructive">{fieldErrors.items}</p>
              )}
              <div className="flex items-center gap-4">
                <Button type="button" variant="ghost" size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  className="text-primary"
                  onClick={() => setItems(prev => [...prev, { ...EMPTY_ITEM }])}>
                  Add item
                </Button>
                {products && products.length > 0 && (
                  <CatalogPicker products={products} onPick={handlePick} />
                )}
              </div>
            </section>
            )}

            {/* ---- Fulfillment ---- */}
            <section className="space-y-3">
              <SectionHeading>Fulfillment</SectionHeading>
              <div role="group" aria-label="Fulfillment method"
                className="inline-flex items-center gap-1 rounded-lg border border-border p-1">
                <Button type="button" size="sm" aria-label="Pickup"
                  aria-pressed={method === 'pickup'}
                  variant={method === 'pickup' ? 'default' : 'ghost'}
                  onClick={() => setMethod('pickup')}>
                  Pickup
                </Button>
                <Button type="button" size="sm" aria-label="Delivery"
                  aria-pressed={method === 'delivery'}
                  variant={method === 'delivery' ? 'default' : 'ghost'}
                  onClick={() => setMethod('delivery')}>
                  Delivery
                </Button>
              </div>
              {method === 'delivery' ? (
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Delivery address" required aria-required
                    error={fieldErrors.address}
                    value={address} onChange={e => setAddress(e.target.value)} />
                  <Input label="Delivery fee" type="number" min={0} step="0.01"
                    value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)} />
                </div>
              ) : (
                <Input label="Pickup location"
                  value={pickupLocation} onChange={e => setPickupLocation(e.target.value)} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <Input label="Delivery / pickup date" id="delivery-date"
                  aria-label="Delivery date" type="datetime-local"
                  value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
              </div>
            </section>

            {/* ---- Payment (commerce); without it the quote-lane amount
                stays editable under its own heading ---- */}
            {!commerceEnabled && quotedAmountField && (
              <section className="space-y-3">
                <SectionHeading>Quote</SectionHeading>
                <div className="grid grid-cols-2 gap-3">{quotedAmountField}</div>
              </section>
            )}
            {commerceEnabled && (
            <section className="space-y-3">
              <SectionHeading>Payment</SectionHeading>
              {isEdit && order && onRecordPayment && (
                <div data-testid="payment-section"
                  className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Recorded payments</span>
                    <Badge variant="secondary" className="capitalize">
                      {order.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  {order.depositCents > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Deposit: {formatCurrency(order.depositCents, order.currency)} — {order.depositPaid ? 'paid' : 'unpaid'}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Payments recorded: {formatCurrency(completedPaymentsCents, order.currency)}
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">Balance due: </span>
                    <span className="font-semibold">{formatCurrency(balanceDueCents, order.currency)}</span>
                  </p>
                  {balanceDueCents > 0 ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="w-28">
                          <Input aria-label="Payment amount" type="number" min={0} step="0.01"
                            disabled={formDirty || recordingPayment}
                            onKeyDown={handlePaymentKeyDown}
                            value={paymentAmount}
                            onChange={e => setPaymentAmount(e.target.value)} />
                        </div>
                        <div className="w-40">
                          <NativeSelect aria-label="Payment method"
                            disabled={formDirty || recordingPayment}
                            onKeyDown={handlePaymentKeyDown}
                            value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="other">Other</option>
                          </NativeSelect>
                        </div>
                        <Button type="button" onClick={handleRecordPayment}
                          isLoading={recordingPayment}
                          disabled={!canRecordPayment}>
                          {recordingPayment ? 'Recording…' : 'Record payment'}
                        </Button>
                      </div>
                      {formDirty && (
                        <p className="text-xs text-muted-foreground">
                          Save your changes first — recording a payment refreshes this order.
                        </p>
                      )}
                      {paymentError && <p className="text-sm text-destructive">{paymentError}</p>}
                    </>
                  ) : (
                    <p className="text-sm text-success">Paid in full</p>
                  )}
                </div>
              )}
              <div>
                {/* Label outside the flex row (htmlFor, not a wrapper label —
                    a wrapping label would associate the preset buttons too)
                    so the presets top-align with the input and stay put when
                    the inline error grows the input column downward. */}
                <label htmlFor="deposit"
                  className="mb-1.5 block text-sm font-medium text-foreground">
                  Deposit
                </label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input id="deposit" type="number" min={0} step="0.01" placeholder="$"
                      error={fieldErrors.deposit}
                      value={deposit} onChange={e => setDeposit(e.target.value)} />
                  </div>
                  {[25, 50].map(pct => (
                    <Button key={pct} type="button" variant="outline" size="sm"
                      aria-label={`Deposit ${pct}%`}
                      disabled={totalCents <= 0}
                      className="h-9"
                      onClick={() => setDeposit((Math.round(totalCents * pct / 100) / 100).toFixed(2))}>
                      {pct}%
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {!isEdit && (
                  <NativeSelect label="Status" value={status}
                    onChange={e => setStatus(e.target.value as typeof status)}>
                    <option value="pending_payment">Pending payment</option>
                    {depositCents > 0 && <option value="deposit_paid">Deposit paid</option>}
                    <option value="fully_paid">Fully paid</option>
                  </NativeSelect>
                )}
                {quotedAmountField}
                {!isEdit && status !== 'pending_payment' && (
                  <NativeSelect label="Payment method" required
                    aria-label="Payment method"
                    error={fieldErrors.payMethod}
                    value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                    <option value="">Select method…</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="other">Other</option>
                  </NativeSelect>
                )}
              </div>
            </section>
            )}

            {/* ---- Notes ---- */}
            <section className="space-y-3">
              <SectionHeading>Notes</SectionHeading>
              <div>
                <label htmlFor="special-instructions"
                  className="mb-1.5 block text-sm font-medium text-foreground">
                  Special instructions
                </label>
                <Textarea id="special-instructions" rows={2}
                  value={instructions} onChange={e => setInstructions(e.target.value)} />
              </div>
              {!isEdit && (
                <div>
                  <label htmlFor="internal-notes"
                    className="mb-1.5 block text-sm font-medium text-foreground">
                    Internal notes
                  </label>
                  <Textarea id="internal-notes" rows={2}
                    value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              )}
            </section>
          </ModalBody>
          {requestError && (
            <p role="alert" className="px-6 pt-3 text-sm text-destructive">{requestError}</p>
          )}
          <ModalFooter className="justify-between">
            <div className="text-sm">
              <span className="text-muted-foreground">Total: </span>
              <span data-testid="new-order-total" className="font-semibold">
                {formatCurrency(totalCents)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline"
                onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={saving}>
                {isEdit ? 'Save changes' : 'Create order'}
              </Button>
            </div>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
